import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import {
    ActionsColumn,
    EnabledColumn,
    IdColumn,
    NameColumn,
    TypeColumn,
} from './columns';
import {
    Box,
    Button,
    Container,
    Flex,
    Group,
    MultiSelect,
    Portal,
    Select,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { MRT_EditActionButtons, MantineReactTable } from 'mantine-react-table';
import { MultiSelectCreatable, StatColumn } from '../../common';
import {
    useDebouncedCallback,
    useDisclosure,
    useLocalStorage,
} from '@mantine/hooks';
import { useMemo, useState } from 'react';

import CampaignGroupStatsTimelineModal from '../../modals/timeline/CampaignGroupStatsTimelineModal';
import { CreateMessageGroupModal } from './modals';
import FiltersMultiSelect from '../../common/filtersMultiSelect/FiltersMultiSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GroupToolbarActions from './toolbars/GroupToolbarActions';
import MessageTable from './messageTable/MessageTable';
import { StatusColumn } from '../campaigns/campaignList/columns';
import classes from './GroupTable.module.css';
import { getMessageGroups } from '../../../http/apiClient';
import { naiveRound } from '../../../utils/text';
import { sumObjectKeys } from '../../../utils/statUtils';
import { useEntityQueryUpdate } from '../../../hooks/useEntityQueryUpdate';
import { usePaginatedQuery } from '../../../http/queries/usePaginatedQuery';
import { useQueryClient } from '@tanstack/react-query';

const MessageGroupsView = ({ ...props }) => {
    const [globalFilter, setGlobalFilter] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const [
        createGroupOpened,
        { open: openCreateModal, close: closeCreateModal },
    ] = useDisclosure();

    const [modalItem, setModalItem] = useState(null);
    const [
        timelineModalOpen,
        { open: openTimelineModal, close: closeTimelineModal },
    ] = useDisclosure();

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) => getMessageGroups({ pagination: queryParams }),
        queryKey: ['message_groups'],
        refetchInterval: 15_0000,
        globalFilter,
        pagination,
        sorting,
    });

    const { data: messageGroups, pagination: dataPagination } =
        paginatedData || {};

    const columns = useMemo(
        () => [
            IdColumn(),
            NameColumn(),
            TypeColumn(),
            StatColumn('Sent', (x) => x.stats?.send),
            StatColumn('Clicks', (x) =>
                sumObjectKeys(x.stats, ['uniqueClick'])
            ),
            StatColumn('Previews', (x) =>
                sumObjectKeys(x.stats, ['uniquePreview'])
            ),
            StatColumn(
                'CTR',
                (x) => {
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound((uniqueClicks / x.stats?.send) * 100, 2);
                },
                true
            ),
            StatColumn('Conversions', (x) => x.stats?.conversion),
            StatColumn(
                'CR',
                (x) => {
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound(
                        (x.stats?.conversion / uniqueClicks) * 100,
                        2
                    );
                },
                true
            ),
        ],
        [messageGroups]
    );

    /*const tableData = useMemo(
        () => (isLoading ? [] : campaigns),
        [campaigns, isLoading]
    );*/

    const tableData = useMemo(
        () => (isLoading ? [] : messageGroups),
        [messageGroups, isLoading]
    );

    const [columnVisibility, setColumnVisibility] = useLocalStorage({
        key: 'message-group-table-visibility', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const [columnSizing, setColumnSizing] = useLocalStorage({
        key: 'message-group-table-sizing', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const handleVisibilityChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnVisibility((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handlSizeChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnSizing((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handleFilterChange = (queryStr) => {
        filterDebounce(queryStr);
    };

    const tableProps = {
        columns: columns,
        data: tableData,
        enableRowActions: true,
        enableColumnResizing: true,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: true,
        enableGlobalFilter: true,
        enableFilters: false,
        enableStickyHeader: true,
        enableDensityToggle: false,
        positionActionsColumn: 'last',
        layoutMode: 'semantic',
        mantineSearchTextInputProps: {
            placeholder: 'Search Message Groups',
            miw: 200,
        },
        mantineTableContainerProps: {
            style: {
                minHeight: '80vh',
            },
        },
        positionGlobalFilter: 'left',
        defaultColumn: {
            minSize: 0,
            maxSize: Number.MAX_SAFE_INTEGER,
        },
        renderDetailPanel: ({ row, table }) => {
            return (
                <>
                    <MessageTable groupId={row.original.id} />
                </>
            );
        },
        renderTopToolbarCustomActions: ({ table }) => (
            <GroupToolbarActions
                filterOptions={[{ label: 'test', value: 'test' }]}
                openCreateModal={openCreateModal}
            />
        ),
        renderRowActions: (props) => (
            <ActionsColumn
                {...props}
                setModalItem={setModalItem}
                openTimelineModal={openTimelineModal}
            />
        ),

        manualPagination: true,
        manualSorting: true,
        createDisplayMode: 'modal', //default ('row', and 'custom' are also available)
        editDisplayMode: 'modal', //default ('row', 'cell', 'table', and 'custom' are also available)
        enableEditing: false,
        enableExpanding: true,
        enableExpandAll: false,
        positionExpandColumn: 'last',
        mantineTableBodyRowProps: ({
            isDetailPanel,
            row,
            staticRowIndex,
            table,
        }) => ({
            className: isDetailPanel ? classes.rowPadding : null,
            onClick: (e) => {
                const INTERACTIVE_SELECTOR =
                    'a[href], button, input:not([type="hidden"]), select, textarea, label, [role="button"]';

                let target = e.target;
                if (target instanceof SVGElement) {
                    target = target.parentElement;
                }

                const interactiveElement =
                    target instanceof HTMLElement &&
                    target.closest(INTERACTIVE_SELECTOR);

                if (interactiveElement) return;

                if (!isDetailPanel) {
                    row.toggleExpanded();
                }
            },
        }),
        onColumnVisibilityChange: handleVisibilityChange,
        onColumnSizingChange: handlSizeChange,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: handleFilterChange,
        rowCount: dataPagination?.totalItems || 0,
        pageCount: dataPagination?.totalPages || 1,
        manualExpanding: true,
        initialState: {
            columnVisibility,
            pagination,
            isLoading,
            showGlobalFilter: true,
        },
        state: {
            isLoading,
            pagination,
            columnVisibility,
            columnSizing,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            sorting,
            density: 'xs',
        },
    };

    return (
        <Container fluid>
            <MantineReactTable {...tableProps} />
            <CreateMessageGroupModal
                opened={createGroupOpened}
                close={closeCreateModal}
            />
            <CampaignGroupStatsTimelineModal
                resource={'messages/groups'}
                resourceId={modalItem?.id}
                title={`Send Timeline - ${modalItem?.name}`}
                opened={timelineModalOpen}
                close={closeTimelineModal}
            />
        </Container>
    );
};
export default MessageGroupsView;
